import { Psychic } from "@/entities/Psychic";
import { UserData } from "@/entities/User";
import { handleError, safelyCaptureException } from "@/shared/lib/helpers/utils";

import { ApiError, BaseProfile, CardInfo, TagData } from "../types";
import { camelToSnakeCase, snakeToCamelCase } from "./utils";

// @ts-expect-error fix later
const EventSource = window.EventSourcePolyfill;

export function getToken() {
  return localStorage.getItem("token");
}

export function getAuthHeader() {
  return { Authorization: `Token ${getToken()}` };
}

interface ApiRequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Abstraction for requests with a standardized response format.
 *
 * @param {string} url
 * @param {object} options for the request, callback is run on the data object if the request is successful
 */
export async function apiRequest(
  url: string,
  options: ApiRequestOptions
): Promise<{
  status: number;
  success: boolean;
  data?: any;
  error?: any;
  err?: ApiError;
}> {
  const { method = "GET", headers, body, signal } = options;
  let response: Response;
  try {
    if (method === "GET") {
      response = await fetch(url, { headers });
    } else {
      const apiHeaders = body instanceof FormData ? headers : { "Content-Type": "application/json", ...headers };
      response = await fetch(url, {
        method,
        headers: apiHeaders,
        body: body instanceof FormData ? body : JSON.stringify(camelToSnakeCase(body)),
        signal,
      });
    }

    let data;
    try {
      data = await response.json();
    } catch (err) {
      /* empty */
    }

    const transformedData = response.ok && data ? snakeToCamelCase(data) : data;

    // An error response will be an array of an error string
    const error = response.ok ? null : data;
    return {
      status: response.status,
      success: response.ok,
      data: transformedData,
      err: response.ok ? null : handleError(data),
      error,
    };
  } catch (error) {
    safelyCaptureException(error);

    return {
      err: handleError(error),
      status: 500,
      success: false,
      error: [String(error)],
    };
  }
}

export async function submitProfile(body: BaseProfile) {
  return apiRequest("/api/profiles", {
    method: "POST",
    body,
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
  });
}

export async function patchProfile(body: BaseProfile | Psychic) {
  return apiRequest("/api/users-me", {
    method: "PATCH",
    body,
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
  });
}

export async function getProfile(userId?: string | number) {
  if (userId !== undefined) {
    return apiRequest(`/api/users/${userId}`, {
      headers: getAuthHeader(),
    });
  }
  return apiRequest("/api/users-me", { headers: getAuthHeader() });
}

export async function getHopingToAchieve() {
  return apiRequest("/api/hopes/", { headers: getAuthHeader() });
}

export async function getUserReadingRequests(userId: string | number) {
  const fields = ["id", "title", "personality_first_name", "status", "created_at", "last_message_time"];

  return apiRequest(`/api/reading_requests?user=${userId}&fields=${fields.join(",")}`, {
    headers: getAuthHeader(),
  });
}

export async function getReadingRequest(id: string) {
  return apiRequest(`/api/reading_requests/${id}`, {
    headers: getAuthHeader(),
  });
}

export async function submitProfilePicture(file: File) {
  const body = new FormData();
  body.append("picture", file);
  return apiRequest("/api/profile/upload_picture", {
    method: "PATCH",
    body,
    headers: getAuthHeader(),
  });
}

export function listenForChannelMessages<T>(url: string, callback: (message: T) => void) {
  const eventSource = new EventSource(window.location.origin + url, {
    headers: getAuthHeader(),
  });
  eventSource.onmessage = (event: MessageEvent<string>) => {
    const data = JSON.parse(event.data);
    callback(snakeToCamelCase(data));
  };
  return () => eventSource.close();
}

export function listenForUserRequestMessages<T>(userId: number, callback: (message: T) => void) {
  return listenForChannelMessages(`/user-messages/${userId}/`, callback);
}

export function listenForPsychicRequestMessages<T>(callback: (message: T) => void) {
  return listenForChannelMessages("/psychic-messages/", callback);
}

export async function getInspirationalQuotes() {
  return apiRequest("/api/quotes", {
    headers: getAuthHeader(),
  });
}

export async function getUsers(filters) {
  const queryParams = Object.entries(filters ?? {})
    .filter(([_, val]) => val)
    .map(([filter, val]) => `${filter}=${val}`);
  const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
  return apiRequest(`/api/users${queryString}`, {
    headers: getAuthHeader(),
  });
}

export async function getPaymentMethods(userId?: string) {
  const baseUrl = "/api/billing/payment_method";
  return apiRequest(`${baseUrl}${userId ? `?user_id=${userId}` : ""}`, {
    headers: getAuthHeader(),
  });
}

export async function addPaymentMethod(cardInfo: CardInfo, makeDefault = false) {
  const { expDate } = cardInfo;
  const [expMonth, expYear] = expDate.split("/");
  const body = {
    ...cardInfo,
    expMonth,
    expYear,
    makeDefaultPaymentMethod: makeDefault,
  };
  return apiRequest("/api/billing/payment_method", {
    method: "POST",
    body,
    headers: getAuthHeader(),
  });
}

export async function deletePaymentMethod(paymentId: string, userId?: string) {
  return apiRequest("/api/billing/payment_method", {
    method: "DELETE",
    body: { paymentMethodId: paymentId, userId },
    headers: getAuthHeader(),
  });
}

export async function makeDefaultPaymentMethod(paymentId: string, userId?: string) {
  return apiRequest("/api/billing/payment_method", {
    method: "PUT",
    body: { paymentMethodId: paymentId, userId },
    headers: getAuthHeader(),
  });
}

export async function getSubscriptionDetails(userId?) {
  const baseUrl = "/api/billing/new_subscription_info";
  return apiRequest(`${baseUrl}${userId ? `?user_id=${userId}` : ""}`, {
    headers: getAuthHeader(),
  });
}

export async function skipSubscription(userId?: string) {
  return apiRequest("/api/subscription/skip_subscription", {
    method: "POST",
    body: { userId },
    headers: getAuthHeader(),
  });
}

export async function unskipSubscription(userId?: string) {
  return apiRequest("/api/subscription/unskip_subscription", {
    method: "POST",
    body: { userId },
    headers: getAuthHeader(),
  });
}

export async function uncancelSubscription(userId?: string) {
  return apiRequest("/api/subscription/uncancel_subscription", {
    method: "POST",
    body: { userId },
    headers: getAuthHeader(),
  });
}

export async function cancelSubscription(userId?: string) {
  return apiRequest("/api/subscription/cancel_subscription", {
    method: "POST",
    body: { userId },
    headers: getAuthHeader(),
  });
}

export async function createSubscription(stripePriceId: string, userId?: string) {
  return apiRequest("/api/subscription/create_subscription", {
    method: "POST",
    body: { stripe_price_id: stripePriceId, user_id: userId },
    headers: getAuthHeader(),
  });
}

export async function upgradeSubscription(toPriceId: string, userId?: string) {
  return apiRequest("/api/subscription/upgrade_subscription", {
    method: "POST",
    body: { to_price_id: toPriceId, user_id: userId },
    headers: getAuthHeader(),
  });
}

export async function downgradeSubscription(toPriceId: string, userId?: string) {
  return apiRequest("/api/subscription/downgrade_subscription", {
    method: "POST",
    body: { to_price_id: toPriceId, user_id: userId },
    headers: getAuthHeader(),
  });
}

export async function sendSupportEmail(content: string) {
  return apiRequest("api/support/email", {
    method: "POST",
    body: { content },
    headers: getAuthHeader(),
  });
}

type InviteFriendEmailInfo = {
  friendName: string;
  friendEmail: string;
  referralCode: string;
};
export async function sendInviteFriendEmail(body: InviteFriendEmailInfo) {
  return apiRequest("api/invite_friend/email", {
    method: "POST",
    body,
    headers: getAuthHeader(),
  });
}

export async function requestPasswordReset(email: string) {
  return apiRequest("/api/auth/password_reset/", {
    method: "POST",
    body: { email },
  });
}

export async function resetPassword(body: { password: string; confirmPassword: string; token: string }) {
  return apiRequest("/api/auth/password_reset/confirm/", {
    method: "POST",
    body,
  });
}

export async function sendTip(amount: string, readingRequestId: string) {
  return apiRequest("/api/give_tip", {
    method: "POST",
    headers: getAuthHeader(),
    body: {
      amount,
      readingRequestId,
    },
  });
}

export async function getGradesList(params) {
  const qs = new URLSearchParams(params).toString();

  return apiRequest(`/api/reading_request_feedback?${qs}`, {
    headers: getAuthHeader(),
  });
}

export async function getReadingFeedback(readingRequestId: string, readingResponseId?: string) {
  let url = `/api/reading_request_feedback?reading_request=${readingRequestId}`;
  if (readingResponseId) {
    url += `&reading_response=${readingResponseId}`;
  }

  return apiRequest(url, {
    headers: getAuthHeader(),
  });
}

export async function provideReadingFeedback(
  readingRequestId: string,
  isPositive: boolean,
  message: string,
  readingResponseId?: string
) {
  const body = {
    type: isPositive ? "like" : "dislike",
    reason: message,
  };

  return apiRequest("/api/reading_request_give_feedback", {
    method: "POST",
    body: readingResponseId ? { ...body, readingResponseId } : { ...body, readingRequestId },
    headers: getAuthHeader(),
  });
}

export async function getActiveReaders() {
  return apiRequest("/api/readers?status=active", {
    headers: getAuthHeader(),
  });
}

export async function updateReaderItem(readerId: string, body) {
  return apiRequest(`/api/readers/${readerId}`, {
    method: "PATCH",
    body,
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
  });
}

export async function getTags() {
  return apiRequest("/api/tags", {
    headers: getAuthHeader(),
  });
}

export async function addNewTag(body: TagData) {
  return apiRequest("/api/tags", {
    method: "POST",
    body,
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
  });
}

export async function updateTagItem(tagId: string, body: TagData) {
  return apiRequest(`/api/tags/${tagId}`, {
    method: "PATCH",
    body,
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
  });
}

export async function getUserTags(userId: string) {
  return apiRequest(`/api/user_tags?user_id=${userId}`, {
    headers: getAuthHeader(),
  });
}

export async function assignUserTag(userId: string, tagId: string) {
  return apiRequest("/api/user_tags", {
    method: "POST",
    body: {
      userId,
      tagId,
    },
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
  });
}

export async function deAssignUserTag(userId: string, tagId: string) {
  return apiRequest("/api/user_tags", {
    method: "DELETE",
    body: {
      userId,
      tagId,
    },
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
  });
}

/**
 * Update user info (method allowed only for staff)
 */
export async function updateUserInfo(userId: number, data: Partial<UserData>) {
  const { gender, ...props } = data;

  return apiRequest(`/api/staff/users/${userId}`, {
    method: "PATCH",
    body: {
      ...props,
      profile: {
        gender,
      },
    },
    headers: getAuthHeader(),
  });
}
